import React, { useState, useEffect, useRef } from "react";
import { FiZap, FiMic, FiImage } from "react-icons/fi";
import { AudioRecorder } from "./AvaliacaoFormComponents";

interface AIInputSectionProps {
  value: string;
  onChange: (value: string) => void;
  onAnalyze: (text?: string) => void;
  onAudioAnalyze?: (audioBlob: Blob) => void;
  onImageAnalyze?: (imageFile: File) => void;
  placeholder: string;
  disabled?: boolean;
  loading?: boolean;
  removeDescription?: boolean;
}

export function AIInputSection({
  value,
  onChange,
  onAnalyze,
  onAudioAnalyze,
  onImageAnalyze,
  placeholder,
  disabled = false,
  loading = false,
  removeDescription = false,
}: AIInputSectionProps) {
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const [audioSent, setAudioSent] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Detecta se é mobile e atualiza o placeholder truncado
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint do Tailwind
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Trunca o placeholder para mobile (aproximadamente 40 caracteres)
  const getPlaceholder = () => {
    if (!isMobile) return placeholder;
    if (placeholder.length <= 40) return placeholder;
    return placeholder.substring(0, 37) + "...";
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && value.trim()) {
      e.preventDefault();
      onAnalyze();
    }
    // Shift+Enter permite quebrar linha (comportamento padrão)
  };

  // Ajusta a altura do textarea automaticamente
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  const handleAudioRecorded = (audioBlob: Blob) => {
    if (onAudioAnalyze) {
      setAudioSent(true);
      onAudioAnalyze(audioBlob);
      // Não fecha o modal imediatamente - aguarda o resultado retornar
      // O modal será fechado quando loading voltar a false
    }
  };

  const handleAudioCancel = () => {
    setShowAudioRecorder(false);
    setAudioSent(false);
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onImageAnalyze) return;

    // Valida se é uma imagem
    if (!file.type.startsWith("image/")) {
      alert("Por favor, selecione um arquivo de imagem.");
      return;
    }

    setImageLoading(true);
    try {
      await onImageAnalyze(file);
    } catch (error) {
      console.error("Erro ao processar imagem:", error);
    } finally {
      // O loading será controlado pelo componente pai através da prop loading
      // Mas resetamos o input para permitir selecionar o mesmo arquivo novamente
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Fecha o modal quando o loading terminar (resultado retornou)
  useEffect(() => {
    if (!loading && showAudioRecorder && audioSent) {
      // Pequeno delay para garantir que o modal de diff já abriu
      const timeoutId = setTimeout(() => {
        setShowAudioRecorder(false);
        setAudioSent(false);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [loading, showAudioRecorder, audioSent]);

  // Sincroniza imageLoading com loading quando loading muda
  useEffect(() => {
    if (!loading) {
      setImageLoading(false);
    }
  }, [loading]);

  // Determina se deve desabilitar elementos (quando imagem está sendo processada)
  const isDisabled = disabled || imageLoading || loading;

  return (
    <div className="space-y-2">
      {showAudioRecorder ? (
        <AudioRecorder
          onAudioRecorded={handleAudioRecorded}
          onCancel={handleAudioCancel}
          autoStart={true}
          loading={loading}
        />
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
          <div className="relative flex-1 min-w-0">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={getPlaceholder()}
              disabled={isDisabled}
              rows={1}
              className="w-full min-h-[48px] max-h-[200px] overflow-y-auto rounded-xl border border-stroke bg-white px-3 py-2.5 pr-10 font-medium text-sm text-gray-900 outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 disabled:opacity-50 disabled:cursor-not-allowed dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 resize-none touch-manipulation sm:min-h-[52px] sm:px-5 sm:py-3.5 sm:pr-12 sm:text-base"
            />
          </div>
          <div className="flex flex-wrap gap-2 sm:flex-row sm:flex-nowrap sm:gap-2">
            {onImageAnalyze && (
              <button
                type="button"
                onClick={handleImageButtonClick}
                disabled={isDisabled}
                className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-xl border border-stroke bg-white px-3 py-2.5 font-semibold text-gray-700 transition-all touch-manipulation active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed dark:border-strokedark dark:bg-boxdark dark:text-gray-300 sm:min-h-0 sm:flex-none sm:gap-2 sm:px-5 sm:py-3.5 md:hover:bg-gray-50 md:hover:shadow-sm md:dark:hover:bg-gray-800/50"
                title="Enviar imagem"
              >
                {imageLoading || loading ? (
                  <div className="h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-gray-700 border-t-transparent dark:border-gray-300 sm:h-4 sm:w-4" />
                ) : (
                  <FiImage size={16} className="flex-shrink-0 sm:w-[18px] sm:h-[18px]" />
                )}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              disabled={isDisabled}
              className="hidden"
            />
            {onAudioAnalyze && (
              <button
                type="button"
                onClick={() => {
                  setShowAudioRecorder(true);
                  setAudioSent(false);
                }}
                disabled={isDisabled}
                className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-xl border border-stroke bg-white px-3 py-2.5 font-semibold text-gray-700 transition-all touch-manipulation active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed dark:border-strokedark dark:bg-boxdark dark:text-gray-300 sm:min-h-0 sm:flex-none sm:gap-2 sm:px-5 sm:py-3.5 md:hover:bg-gray-50 md:hover:shadow-sm md:dark:hover:bg-gray-800/50"
                title="Gravar áudio"
              >
                <FiMic size={16} className="flex-shrink-0 sm:w-[18px] sm:h-[18px]" />
              </button>
            )}
            {value.trim() && (
              <button
                type="button"
                onClick={() => onAnalyze()}
                disabled={isDisabled || !value.trim()}
                className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-purple-600 to-purple-700 px-3 py-2.5 font-semibold text-white shadow-sm transition-all touch-manipulation active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed sm:min-h-0 sm:flex-none sm:gap-2 sm:px-5 sm:py-3.5 md:hover:scale-105 md:hover:shadow-md disabled:md:hover:scale-100"
                title="Gerar preenchimento com IA"
              >
                <FiZap size={16} className="flex-shrink-0 sm:w-[18px] sm:h-[18px]" />
              </button>
            )}
          </div>
        </div>
      )}
      {!removeDescription && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Informe as medidas (peso, altura, dobras, etc), envie uma imagem ou
          grave um áudio. A IA preenche campos que identificar.
        </p>
      )}
    </div>
  );
}
